import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Html5Qrcode } from "html5-qrcode";

// @mui material components
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

import { sotApi } from "api/sot";
import { TRANG_THAI_KHOA } from "lib/trangThaiSot";
import { MAU } from "assets/theme/avocadoBrand";
import { useDialogFullManHinh } from "lib/dungDialogFullManHinh";
import { dungScannerAnToan, tinhQrBox } from "lib/dungCameraQr";

// ID riêng cho Dialog này — tránh trùng id DOM nếu 2 nơi từng render cùng lúc
// (không xảy ra thực tế, nhưng an toàn hơn).
const CAMERA_BOX_ID = "vung-quet-sot-camera";

// Dialog quét QR để mở thêm 1 Sọt MỚI ngay trong FormThuMua, khi sọt đang cân
// đầy giữa chừng. Không điều hướng trang (navigate) và không cần ngoại cảnh
// (chủ vườn/vườn) — chỉ trả Sọt vừa quét được về cho form gọi nó.
function QuetSotDialog({ open, onDong, onQuetDuoc, maSotHienTai }) {
  const fullManHinh = useDialogFullManHinh();
  const [maQr, setMaQr] = useState("");
  const [loi, setLoi] = useState("");
  const [dangTai, setDangTai] = useState(false);
  const [dungCamera, setDungCamera] = useState(true);
  const [loiCamera, setLoiCamera] = useState("");
  const [dangKhoiDongCamera, setDangKhoiDongCamera] = useState(true);
  // Dialog dựng nội dung qua Portal + hiệu ứng transition (Fade) — div
  // id=CAMERA_BOX_ID có thể chưa gắn xong vào DOM ngay lúc "open" vừa lên
  // true. Chỉ bắt đầu camera SAU KHI transition mở hoàn tất (TransitionProps
  // onEntered bên dưới) — không thể coi "open = true" là "div đã có trong DOM".
  const [dialogDaMoXong, setDialogDaMoXong] = useState(false);

  const qrRef = useRef(null);
  const dangXuLyRef = useRef(false);
  const quetGanNhatRef = useRef({ ma: null, luc: 0 });

  // Dialog đóng/mở lại là tạo mới component con (MUI Dialog không giữ children
  // trong DOM khi open=false) — reset state về mặc định mỗi lần mở lại.
  useEffect(() => {
    if (!open) return;
    setMaQr("");
    setLoi("");
    setDangTai(false);
    setDungCamera(true);
    setLoiCamera("");
    setDialogDaMoXong(false);
  }, [open]);

  async function tra(qrCode) {
    const ma = qrCode.trim();
    const gio = Date.now();
    // Camera đọc ~10 khung hình/giây: bỏ qua các lần quét trùng mã trong lúc
    // QR vẫn còn nằm trong khung hình, tránh spam truy vấn Firestore.
    if (quetGanNhatRef.current.ma === ma && gio - quetGanNhatRef.current.luc < 3000) return;
    quetGanNhatRef.current = { ma, luc: gio };

    if (dangXuLyRef.current) return;
    dangXuLyRef.current = true;
    setLoi("");
    setDangTai(true);
    try {
      const sot = await sotApi.theoQr(ma);
      if (TRANG_THAI_KHOA.includes(sot.trangThai)) {
        setLoi(`Sọt ${sot.maSot} đã đóng, không thể thu mua thêm.`);
        return;
      }
      if (sot.id === maSotHienTai) {
        setLoi(`Sọt ${sot.maSot} chính là sọt đang dùng — quét một sọt khác.`);
        return;
      }
      await dungScannerAnToan(qrRef.current);
      qrRef.current = null;
      onQuetDuoc(sot);
    } catch (err) {
      setLoi(err.message || "Không tìm thấy Sọt với mã QR này.");
    } finally {
      dangXuLyRef.current = false;
      setDangTai(false);
    }
  }

  function xacNhanNhapTay(e) {
    e.preventDefault();
    tra(maQr);
  }

  // Hai nút chuyển chế độ chỉ có tác dụng khi ĐỔI sang chế độ còn lại — bấm
  // vào chế độ đang bật là không làm gì.
  function chuyenSangQuetQr() {
    if (dungCamera) return;
    setLoi("");
    setDungCamera(true);
  }

  async function chuyenSangNhapTay() {
    if (!dungCamera) return;
    // Dừng camera trước khi ẩn UI — không unmount #CAMERA_BOX_ID (chỉ CSS),
    // tránh AbortError "play() was interrupted because the media was removed".
    await dungScannerAnToan(qrRef.current);
    qrRef.current = null;
    setDungCamera(false);
  }

  async function dongDialog() {
    await dungScannerAnToan(qrRef.current);
    qrRef.current = null;
    onDong();
  }

  useEffect(() => {
    if (!dialogDaMoXong || !dungCamera) return undefined;

    let huy = false;
    let qr;
    try {
      // Html5Qrcode ném lỗi ĐỒNG BỘ (throw ngay tại đây, không phải Promise
      // reject) nếu chưa tìm thấy div id=CAMERA_BOX_ID trong DOM — khác với
      // trang thường (div luôn có sẵn), ở đây div nằm trong Dialog nên có thể
      // chưa kịp gắn vào DOM lúc effect này chạy (MUI
      // Dialog dựng nội dung qua Portal). App không có ErrorBoundary nào nên
      // lỗi đồng bộ không bắt sẽ crash trắng cả trang — bắt buộc try/catch ở
      // đây, không được để lọt ra ngoài như các lỗi bất đồng bộ của .start().
      qr = new Html5Qrcode(CAMERA_BOX_ID);
    } catch (err) {
      setDangKhoiDongCamera(false);
      setDungCamera(false);
      setLoiCamera(
        `Không mở được camera (${err}). Hãy nhập mã QR bằng tay bên dưới, hoặc đóng rồi thử lại.`
      );
      return undefined;
    }
    qrRef.current = qr;
    setLoiCamera("");
    setDangKhoiDongCamera(true);

    qr.start(
      { facingMode: "environment" },
      {
        fps: 10,
        // advanced/focusMode: nhiều điện thoại Android mặc định không auto-focus
        // liên tục khi mở camera qua getUserMedia, khiến mã QR in nhỏ/cầm gần
        // luôn bị mờ và không bao giờ đọc được dù camera "chạy" bình thường.
        // Trình duyệt không hỗ trợ constraint này sẽ tự bỏ qua, không lỗi.
        // (phải đặt trong videoConstraints — cameraIdOrConfig ở start() chỉ
        // cho phép đúng 1 key nên không nhét "advanced" vào đó được).
        videoConstraints: {
          facingMode: "environment",
          advanced: [{ focusMode: "continuous" }],
        },
        // Khung vuông cố định 250px có thể to hơn khung hình camera thật trên
        // màn hình hẹp/camera phân giải thấp — lúc đó thư viện không quét
        // được gì cả. Tính theo % kích thước khung hình thật để luôn vừa.
        qrbox: tinhQrBox,
        // Bộ giải mã JS thuần (zxing) đi kèm thư viện yếu hơn hẳn API quét mã
        // vạch gốc của trình duyệt (BarcodeDetector) — máy nào hỗ trợ (Chrome/
        // Edge Android phần lớn) sẽ tự chuyển sang dùng, máy không hỗ trợ vẫn
        // rơi về cách cũ, không lỗi gì thêm.
        experimentalFeatures: { useBarCodeDetectorIfSupported: true },
      },
      (maQuetDuoc) => tra(maQuetDuoc),
      () => {} // lỗi decode mỗi frame không tìm thấy QR — bỏ qua, không phải lỗi thật
    )
      .then(() => {
        if (huy) {
          // Đã đóng Dialog / đổi chế độ trước khi start() kịp xong — lúc
          // cleanup có thể chạy khi start() còn bay, phải tự dừng ở đây nếu
          // không camera sẽ chạy ngầm vô thời hạn.
          dungScannerAnToan(qr);
          return;
        }
        setDangKhoiDongCamera(false);
      })
      .catch((err) => {
        if (huy) return;
        setDangKhoiDongCamera(false);
        setDungCamera(false);
        const lyDo = err?.message ?? err;
        setLoiCamera(
          `Không mở được camera (${lyDo}). Hãy cấp quyền camera cho trình duyệt, hoặc nhập mã QR bằng tay bên dưới.`
        );
      });

    return () => {
      huy = true;
      // Luôn gọi dừng — không chỉ khi isScanning: start() có thể còn đang bay.
      dungScannerAnToan(qr);
      if (qrRef.current === qr) qrRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dungCamera, dialogDaMoXong]);

  return (
    <Dialog
      open={open}
      onClose={dongDialog}
      fullScreen={fullManHinh}
      maxWidth="xs"
      fullWidth
      TransitionProps={{
        // Chỉ coi là "đã mở xong" (an toàn để Html5Qrcode gắn vào DOM) sau
        // khi hiệu ứng mở Dialog hoàn tất — tránh đúng lỗi mô tả ở effect
        // trên (div chưa kịp gắn khi vừa open=true).
        onEntered: () => setDialogDaMoXong(true),
        onExited: () => setDialogDaMoXong(false),
      }}
    >
      <DialogContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <MDBox>
            <MDTypography
              variant="overline"
              sx={{ color: MAU.pit, fontWeight: 700, letterSpacing: "0.1em" }}
            >
              Chuyển sọt
            </MDTypography>
            <MDTypography variant="h6" component="h2">
              Quét sọt mới
            </MDTypography>
          </MDBox>
          <IconButton onClick={dongDialog} size="small">
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
        <MDTypography variant="button" color="text" display="block" mb={2}>
          Quét mã QR dán trên sọt tiếp theo để tiếp tục cân — các dòng cân sau đó sẽ gắn vào sọt
          này.
        </MDTypography>

        <MDBox display="flex" gap={1} p={0.5} mb={2.5} borderRadius="lg" bgColor="grey-200">
          <MDButton
            fullWidth
            variant={dungCamera ? "gradient" : "text"}
            color={dungCamera ? "info" : "dark"}
            onClick={chuyenSangQuetQr}
          >
            <Icon sx={{ mr: 0.75 }}>qr_code_scanner</Icon>
            Quét QR
          </MDButton>
          <MDButton
            fullWidth
            variant={dungCamera ? "text" : "gradient"}
            color={dungCamera ? "dark" : "info"}
            onClick={chuyenSangNhapTay}
          >
            <Icon sx={{ mr: 0.75 }}>keyboard</Icon>
            Nhập tay
          </MDButton>
        </MDBox>

        {/* Giữ #CAMERA_BOX_ID luôn trong DOM — chỉ ẩn bằng CSS khi nhập tay,
            tránh AbortError khi <video> bị gỡ giữa lúc play()/start(). */}
        <MDBox sx={{ display: dungCamera ? "block" : "none" }}>
          {/* Không ép aspect-ratio/max-height cho khung này: html5-qrcode tự
              chèn <video> theo tỉ lệ camera thật, ép khung sẽ làm hình bị cắt
              lệch với vùng quét mà thư viện vẽ lên. */}
          <MDBox
            id={CAMERA_BOX_ID}
            sx={{
              width: "100%",
              minHeight: "250px",
              borderRadius: "0.75rem",
              overflow: "hidden",
              backgroundColor: MAU.ink,
            }}
          />
          <MDBox display="flex" alignItems="center" gap={0.75} mt={1}>
            <Icon sx={{ color: MAU.pit, fontSize: "1rem" }}>center_focus_strong</Icon>
            <MDTypography variant="caption" color="text">
              {dangKhoiDongCamera
                ? "Đang mở camera..."
                : "Đưa mã QR trên sọt mới vào giữa khung hình."}
            </MDTypography>
          </MDBox>
        </MDBox>

        {!dungCamera && (
          <MDBox component="form" onSubmit={xacNhanNhapTay}>
            {loiCamera && (
              <MDBox mb={2}>
                <MDAlert color="warning">{loiCamera}</MDAlert>
              </MDBox>
            )}
            <MDInput
              label="Nhập/quét mã QR sọt mới"
              value={maQr}
              onChange={(e) => setMaQr(e.target.value)}
              fullWidth
              autoFocus
              required
            />
            {loi && (
              <MDBox mt={2}>
                <MDAlert color="error">{loi}</MDAlert>
              </MDBox>
            )}
            <MDBox mt={2.5}>
              <MDButton type="submit" variant="gradient" color="info" fullWidth disabled={dangTai}>
                {dangTai ? "Đang kiểm tra..." : "Xác nhận"}
              </MDButton>
            </MDBox>
          </MDBox>
        )}

        {dungCamera && loi && (
          <MDBox mt={2}>
            <MDAlert color="error">{loi}</MDAlert>
          </MDBox>
        )}
      </DialogContent>
    </Dialog>
  );
}

QuetSotDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onDong: PropTypes.func.isRequired,
  onQuetDuoc: PropTypes.func.isRequired,
  maSotHienTai: PropTypes.string,
};

QuetSotDialog.defaultProps = {
  maSotHienTai: null,
};

export default QuetSotDialog;
